import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { useModifiers } from '../../../styles/hooks/useModifiers';
import type { RippleProps } from '../../ripple/Ripple';
import { Ripple, RippleRef } from '../../ripple/Ripple';

import styles from './Badge.module.css';

export enum BadgeSize {
  Dot = 'dot',
  Tiny = 'tiny',
  Mini = 'mini',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface BadgeProps<Value extends string | number> extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Content to display in the badge, takes priority over the value prop */
  children?: (value: Value) => ReactNode;
  /** Value to display in the badge */
  value?: Value;
  /** Size of the badge, 'dot' and 'tiny' do not display the value */
  size: BadgeSize;
  /** Whether the badge should have an impact effect (red color) */
  impact?: boolean;
  /** direction in which badge will grow, `center` by default */
  grow?: 'center' | 'start' | 'end';
  /** Additional class names to apply to the badge, for positioning purposes */
  className?: string;
  /** The ripple variant which needs to be shown*/
  ripple?: RippleProps['variant'];
  /** Trigger animation on each time when value change */
  triggerOnChange?: boolean;
  /** Trigger animation only single time when component mounts */
  triggerOnce?: boolean;
}

/**
 * Component to be used for displaying a badge in other controls.
 * */
export const Badge = <Value extends string | number>({
  children,
  value,
  impact,
  size,
  className,
  grow = 'center',
  ripple,
  triggerOnChange = false,
  triggerOnce = true,
  ...rest
}: BadgeProps<Value>): ReactElement => {
  const rippleRef = useRef<RippleRef>(null);

  useEffect(() => {
    if (value !== undefined && triggerOnChange) {
      const rippleHandle = rippleRef.current;
      rippleHandle?.triggerAnimation();

      return (): void => rippleHandle?.stopAnimation();
    }
  }, [triggerOnChange, value]);

  useEffect(() => {
    if (value !== undefined && triggerOnce) {
      const rippleHandle = rippleRef.current;
      rippleHandle?.triggerAnimation();

      return (): void => rippleHandle?.stopAnimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerOnce]);

  const badgeWrapperWithMods = useModifiers(
    styles,
    'badge__wrapper',
    {
      [size]: true,
      [grow]: true,
    },
    { withBaseClass: true, extraClassName: className }
  );

  const badgeWithMods = useModifiers(
    styles,
    'badge',
    {
      impact,
      elevated:
        [BadgeSize.Mini, BadgeSize.Small, BadgeSize.Medium, BadgeSize.Large].includes(size)
    },
    { withBaseClass: true }
  );

  const badgeContent = (
    <span data-testid="badge-content" className={badgeWithMods}>
      {size !== BadgeSize.Dot && size !== BadgeSize.Tiny
        ? (children && value ? children(value) : value) || value
        : null}
    </span>
  );

  return (
    <span className={badgeWrapperWithMods} {...rest}>
      {ripple ? (
        <Ripple variant={ripple} ref={rippleRef}>
          {badgeContent}
        </Ripple>
      ) : (
        badgeContent
      )}
    </span>
  );
};
